import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import * as Yup from 'yup';
import { ProjectInstallationStatusEnum } from '@api/enum';
import {
    getProjectInstallationById,
    getProjectInstallationTimeline,
    updateProjectInstallation,
} from '@api/services/project-installation.service';
import { IBusinessDevelopmentUser } from '@api/models';
import useDataQuery from '@hooks/use-data-query';

type TProjectInstallationFormValues = {
    name: string;
    poc: {
        name: string;
        email: string;
        phoneNumber: number | string;
        designation: string;
    }[];
    location: {
        country: string;
        latitude: string;
        longitude: string;
        googleMapsLink: string;
        city: string;
    };
    contract: {
        startDate: string;
        endDate: string;
    };
    // projectMilestone: boolean;
    status: ProjectInstallationStatusEnum;
    businessDevelopmentUser?: string;

    files: File[];
};

const useProjectInstallationUpdateForm = (
    clientInstallationId: string,
    projectInstallationId: string,
    initialData: any
) => {
    const schema = Yup.object({
        name: Yup.string().required('Name is required'),
        poc: Yup.array().of(
            Yup.object({
                name: Yup.string().required('Name is required'),
                email: Yup.string().email('Invalid email').required('Email is required'),
                phoneNumber: Yup.number().nullable(),
                designation: Yup.string().nullable(),
            })
        ),
        location: Yup.object({
            country: Yup.string().required('Country is required'),
            city: Yup.string().required('City is required'),
            latitude: Yup.string().optional(),
            longitude: Yup.string().optional(),
            googleMapsLink: Yup.string().url('Enter a valid URL').optional(),
        }),
        businessDevelopmentUser: Yup.string().required('This field is required'),

        contract: Yup.object({
            startDate: Yup.string()
                .nullable()
                .test('valid-startDate', 'Invalid start date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                }),

            endDate: Yup.string()
                .nullable()
                .test('valid-endDate', 'Invalid end date', (value) => {
                    if (!value) return true; // allow empty string or null
                    return moment(value, 'YYYYMMDD', true).isValid();
                })
                .test('end-after-start', 'End date must be same or after start date', function (endDate) {
                    const { startDate } = this.parent;

                    if (!startDate || !endDate) return true; // Allow empty or null start or end date
                    const start = moment(startDate, 'YYYYMMDD', true);
                    const end = moment(endDate, 'YYYYMMDD', true);

                    // Ensuring the end date is same or after the start date
                    return end.isSameOrAfter(start, 'day');
                }),
        }),
        // projectMilestone: Yup.boolean().required(),
        status: Yup.mixed<ProjectInstallationStatusEnum>()
            .oneOf(Object.values(ProjectInstallationStatusEnum))
            .required('Status is required'),
        files: Yup.array().of(Yup.mixed()),
    });

    const initialValues: TProjectInstallationFormValues = {
        name: initialData?.name || '',
        poc: initialData?.poc || [{ name: '', email: '', phoneNumber: '', designation: '' }],
        location: {
            country: initialData?.location?.country || '',
            city: initialData?.location?.city || '',
            latitude: initialData?.location?.latitude || '',
            longitude: initialData?.location?.longitude || '',
            googleMapsLink: initialData?.location?.googleMapsLink || '',
        },
        contract: {
            startDate: initialData?.contract?.startDate
                ? moment(initialData.contract.startDate).format('YYYYMMDD')
                : '',
            endDate: initialData?.contract?.endDate ? moment(initialData.contract.endDate).format('YYYYMMDD') : '',
        },
        businessDevelopmentUser: (initialData.businessDevelopmentUser as IBusinessDevelopmentUser)._id,

        // projectMilestone: initialData?.projectMilestone || false,
        status: initialData?.status || ProjectInstallationStatusEnum.PENDING,
        files: [],
    };

    const mutation = useMutation((payload: any) =>
        updateProjectInstallation(clientInstallationId, projectInstallationId, payload)
    );

    const submit = async (values: TProjectInstallationFormValues) => {
        const payload = {
            name: values.name,
            status: values.status,
            businessDevelopmentUser: values.businessDevelopmentUser || '',
            poc: values.poc.map((poc) => ({
                name: poc.name,
                email: poc.email,
                phoneNumber: poc.phoneNumber.toString(),
                designation: poc.designation || '',
            })),
            location: {
                country: values.location.country,
                city: values.location.city,
                latitude: values.location.latitude,
                longitude: values.location.longitude,
                googleMapsLink: values.location.googleMapsLink,
            },
            contract: {
                startDate: values.contract.startDate,
                endDate: values.contract.endDate,
            },
        };

        const data = await mutation.mutateAsync(payload); // Send the payload as JSON
        return data;
    };

    return {
        initialValues,
        schema,
        isSubmitting: mutation.isLoading,
        submit,
    };
};

export default useProjectInstallationUpdateForm;
